import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { saFetch, SALoading, fmtDateTime, fmtDate, inputCls, btnOutline, btnPrimary, PayBadge } from "./saCommon";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MagnifyingGlass, Eye, CaretLeft, CaretRight, Headset, CircleNotch } from "@phosphor-icons/react";

export default function SAEmpresas() {
  const [data, setData] = useState(null);
  const [plans, setPlans] = useState([]);
  const [q, setQ] = useState("");
  const [plan, setPlan] = useState("all");
  const [status, setStatus] = useState("all");
  const [sort, setSort] = useState("last_seen");
  const [page, setPage] = useState(1);
  const [profile, setProfile] = useState(null);
  const [profileId, setProfileId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState({ plan_id: "", courtesy_days: "", access_expires: "", custom_price: "", internal_notes: "" });

  const load = useCallback(() => {
    const params = new URLSearchParams({ q, sort, order: "desc", page: String(page), per_page: "10" });
    if (plan !== "all") params.set("plan", plan);
    if (status !== "all") params.set("status", status);
    saFetch(`/superadmin/companies-v2?${params}`).then(setData).catch((e) => toast.error(e.message));
  }, [q, plan, status, sort, page]);

  useEffect(load, [load]);
  useEffect(() => { saFetch("/superadmin/plans").then(setPlans).catch(() => {}); }, []);

  const openProfile = async (tenantId) => {
    setProfileId(tenantId);
    try {
      const d = await saFetch(`/superadmin/companies/${tenantId}/profile`);
      setProfile(d);
      setEditForm({
        plan_id: d.subscription?.plan_id || "",
        courtesy_days: "",
        access_expires: (d.subscription?.access_expires || "").slice(0, 10),
        custom_price: d.subscription?.custom_price ?? "",
        internal_notes: d.company?.internal_notes || "",
      });
    } catch (e) {
      toast.error(e.message);
    }
  };

  const saveProfile = async (fields) => {
    setSaving(true);
    try {
      await saFetch(`/superadmin/companies/${profileId}/v2`, { method: "PATCH", body: JSON.stringify(fields) });
      toast.success("Empresa actualizada");
      openProfile(profileId);
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (c) => {
    try {
      await saFetch(`/superadmin/companies/${c.tenant_id}`, { method: "PATCH", body: JSON.stringify({ active: !c.active }) });
      toast.success(c.active ? "Acceso suspendido" : "Acceso activado");
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const changePlan = async (c, planId) => {
    try {
      await saFetch(`/superadmin/companies/${c.tenant_id}/v2`, { method: "PATCH", body: JSON.stringify({ plan_id: planId }) });
      toast.success("Plan actualizado");
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const supportEnter = async () => {
    try {
      const r = await saFetch(`/superadmin/companies/${profileId}/support-enter`, { method: "POST" });
      toast.success(r.detail);
      openProfile(profileId);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.per_page)) : 1;

  return (
    <div data-testid="sa-empresas">
      <div className="mb-6">
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-foreground">Empresas</h1>
        <p className="text-sm text-muted-foreground mt-1">{data ? `${data.total} registradas` : "Gestiona las empresas del sistema"}</p>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={15} />
          <Input placeholder="Buscar por empresa, RTN o responsable..." className={`${inputCls} pl-9`} data-testid="sa-search"
            value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} />
        </div>
        <Select value={plan} onValueChange={(v) => { setPlan(v); setPage(1); }}>
          <SelectTrigger className={`${inputCls} w-40`} data-testid="sa-filter-plan"><SelectValue placeholder="Plan" /></SelectTrigger>
          <SelectContent className="bg-popover border-border">
            <SelectItem value="all">Todos los planes</SelectItem>
            <SelectItem value="free">Gratuito</SelectItem>
            {plans.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
          <SelectTrigger className={`${inputCls} w-40`} data-testid="sa-filter-status"><SelectValue placeholder="Estado" /></SelectTrigger>
          <SelectContent className="bg-popover border-border">
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="active">Acceso activo</SelectItem>
            <SelectItem value="suspended">Suspendidas</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className={`${inputCls} w-44`} data-testid="sa-sort"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-popover border-border">
            <SelectItem value="last_seen">Último acceso</SelectItem>
            <SelectItem value="created_at">Fecha de registro</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {!data ? (
        <SALoading />
      ) : (
        <>
          <div className="border border-border rounded-sm overflow-x-auto bg-card">
            <table className="w-full text-sm min-w-[900px]" data-testid="sa-companies-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground border-b border-border">
                  <th className="p-3 font-normal">Empresa</th>
                  <th className="p-3 font-normal">RTN</th>
                  <th className="p-3 font-normal">Empleados</th>
                  <th className="p-3 font-normal">Último acceso</th>
                  <th className="p-3 font-normal">Plan</th>
                  <th className="p-3 font-normal">Acceso</th>
                  <th className="p-3 font-normal text-right">Perfil</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.items.map((c) => (
                  <tr key={c.tenant_id} className="hover:bg-muted/70 transition-colors duration-200" data-testid={`sa-row-${c.tenant_id}`}>
                    <td className="p-3">
                      <p className="text-foreground font-medium">{c.name || "Sin nombre"}</p>
                      <p className="text-xs text-muted-foreground">{c.correo || c.last_user || ""}</p>
                    </td>
                    <td className="p-3 text-foreground/80">{c.rtn || "—"}</td>
                    <td className="p-3 text-foreground/80">{c.employees_count ?? 0}</td>
                    <td className="p-3 text-foreground/80">{fmtDateTime(c.last_seen)}</td>
                    <td className="p-3">
                      <Select value={c.subscription?.plan_id || "free"} onValueChange={(v) => changePlan(c, v === "free" ? "" : v)}>
                        <SelectTrigger className={`${inputCls} h-8 w-36`} data-testid={`sa-plan-${c.tenant_id}`}><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-popover border-border">
                          <SelectItem value="free">Gratuito</SelectItem>
                          {plans.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <Switch checked={c.active !== false} onCheckedChange={() => toggleActive(c)} data-testid={`sa-active-${c.tenant_id}`} />
                        <span className={`text-xs ${c.active !== false ? "text-emerald-500" : "text-red-400"}`}>{c.active !== false ? "Activa" : "Suspendida"}</span>
                      </div>
                    </td>
                    <td className="p-3 text-right">
                      <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground hover:bg-accent" data-testid={`sa-profile-${c.tenant_id}`} onClick={() => openProfile(c.tenant_id)}>
                        <Eye className="mr-1.5" size={14} /> Ver
                      </Button>
                    </td>
                  </tr>
                ))}
                {data.items.length === 0 && (
                  <tr><td colSpan={7} className="p-10 text-center text-muted-foreground">Sin resultados para estos filtros.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between mt-3">
            <span className="text-xs text-muted-foreground">Página {data.page} de {totalPages} · {data.total} empresas</span>
            <div className="flex gap-2">
              <Button variant="outline" size="icon" className={btnOutline} disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="sa-page-prev"><CaretLeft size={15} /></Button>
              <Button variant="outline" size="icon" className={btnOutline} disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="sa-page-next"><CaretRight size={15} /></Button>
            </div>
          </div>
        </>
      )}

      <Dialog open={!!profile} onOpenChange={() => setProfile(null)}>
        <DialogContent aria-describedby={undefined} className="bg-card border-border rounded-sm sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-foreground">Perfil de empresa</DialogTitle>
          </DialogHeader>
          {profile && (
            <div className="space-y-5" data-testid="sa-company-profile">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-heading text-xl font-bold text-foreground">{profile.company.name || "Sin nombre"}</p>
                  <p className="text-sm text-muted-foreground">RTN {profile.company.rtn || "—"} · {profile.company.correo || "sin correo"}</p>
                  <p className="text-xs text-muted-foreground mt-1">Registrada: {fmtDate(profile.company.created_at)} · Último acceso: {fmtDateTime(profile.company.last_seen)}</p>
                </div>
                <Button variant="outline" size="sm" className={btnOutline} data-testid="sa-support-enter" onClick={supportEnter}>
                  <Headset className="mr-2" size={15} /> Modo soporte
                </Button>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[["Empleados", profile.company.employees_count ?? 0], ["Plan", profile.plan?.name || "Gratuito"],
                  ["Ciclo", profile.subscription?.billing_cycle === "annual" ? "Anual" : "Mensual"],
                  ["Vence", fmtDate(profile.subscription?.current_period_end)]].map(([l, v]) => (
                  <div key={l} className="border border-border rounded-sm p-3">
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{l}</p>
                    <p className="font-heading text-lg font-semibold text-foreground mt-0.5">{v}</p>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">Plan de suscripción</Label>
                  <Select value={editForm.plan_id || "free"} onValueChange={(v) => setEditForm({ ...editForm, plan_id: v === "free" ? "" : v })}>
                    <SelectTrigger className={inputCls} data-testid="sa-profile-plan"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-popover border-border">
                      <SelectItem value="free">Gratuito</SelectItem>
                      {plans.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">Días de cortesía</Label>
                  <Input type="number" min="0" className={inputCls} placeholder="Ej. 7" data-testid="sa-courtesy-days"
                    value={editForm.courtesy_days} onChange={(e) => setEditForm({ ...editForm, courtesy_days: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">Fecha de vencimiento</Label>
                  <Input type="date" className={inputCls} data-testid="sa-access-expires"
                    value={editForm.access_expires} onChange={(e) => setEditForm({ ...editForm, access_expires: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">Precio especial (opcional)</Label>
                  <Input type="number" step="0.01" min="0" className={inputCls} placeholder="Precio personalizado" data-testid="sa-custom-price"
                    value={editForm.custom_price} onChange={(e) => setEditForm({ ...editForm, custom_price: e.target.value })} />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Observaciones internas (solo Superadmin)</Label>
                <Textarea className={`${inputCls} min-h-[70px]`} data-testid="sa-internal-notes"
                  value={editForm.internal_notes} onChange={(e) => setEditForm({ ...editForm, internal_notes: e.target.value })} />
              </div>
              <Button onClick={() => saveProfile({
                plan_id: editForm.plan_id || undefined,
                courtesy_days: editForm.courtesy_days ? Number(editForm.courtesy_days) : undefined,
                access_expires: editForm.access_expires || undefined,
                custom_price: editForm.custom_price !== "" ? Number(editForm.custom_price) : undefined,
                internal_notes: editForm.internal_notes,
              })} disabled={saving} className={btnPrimary} data-testid="sa-profile-save">
                {saving && <CircleNotch className="animate-spin mr-2" size={15} />} Guardar cambios
              </Button>

              <div>
                <h4 className="font-heading text-sm font-bold text-foreground mb-2">Historial de pagos ({profile.payments.length})</h4>
                {profile.payments.length === 0 ? (
                  <p className="text-xs text-muted-foreground">Sin pagos registrados.</p>
                ) : (
                  <div className="border border-border rounded-sm divide-y divide-border max-h-44 overflow-y-auto">
                    {profile.payments.map((p) => (
                      <div key={p.id} className="flex items-center justify-between p-2.5 text-sm">
                        <span className="text-foreground">{p.plan_name} · {p.billing_cycle === "annual" ? "Anual" : "Mensual"}</span>
                        <span className="flex items-center gap-2">
                          <span className="text-foreground/80">L {Number(p.total || 0).toFixed(2)}</span>
                          <PayBadge status={p.status} />
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <h4 className="font-heading text-sm font-bold text-foreground mb-2">Auditoría reciente</h4>
                {profile.audit.length === 0 ? (
                  <p className="text-xs text-muted-foreground">Sin registros.</p>
                ) : (
                  <ul className="divide-y divide-border border border-border rounded-sm max-h-40 overflow-y-auto">
                    {profile.audit.map((a) => (
                      <li key={a.id} className="p-2.5">
                        <p className="text-xs text-foreground">{a.detail}</p>
                        <p className="text-[11px] text-muted-foreground">{a.actor} · {fmtDateTime(a.created_at)}</p>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
